import * as React from 'react';
import { useEffect, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import { codeFontFamily } from '../../../../themes/codeTokens';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ObjectExpressionModel } from '../../Models/ObjectExpressionModel';
import { ObjectPropertyType } from '../../Models/ObjectPropertyType';
import { ObjectSchemaType } from '../../Models/ObjectSchemaType';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import getPropertyTypeName from '../../Models/getPropertyTypeName';
import { ExpressionCopyPasteActions } from './ExpressionCopyPasteActions';
import ExpressionObjectNestedPlaceholder from './ExpressionObjectNestedPlaceholder';
import ExpressionPlaceholder from './ExpressionPlaceholder';
import { getCaseInsensitivePropertyKey } from './caseInsensitiveProperty';
import { getObjectExpression } from './getObjectExpression';
import { getPropertyParameterMetadata } from './getPropertyParameterMetadata';
import { createObjectMapExpression, updateObjectMapExpression } from './mapExpressionHelpers';

const PROPERTY_COLUMN_SX = { maxWidth: 260, overflowWrap: 'anywhere' };
const ASSIGNMENT_COLUMN_SX = { width: 28, minWidth: 28, maxWidth: 28, px: 0 };
const ACTIONS_COLUMN_SX = { width: 96, minWidth: 96, maxWidth: 96 };
const ACTIONS_CELL_CONTENT_SX = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25, '& .MuiIconButton-root': { m: 0, p: 0.5 } };
const ASSIGNMENT_ARROW_SX = { display: 'block', mx: 'auto', fontSize: 18, color: 'text.secondary', opacity: 0.75 };

type ExpressionObjectPlaceholderProps = {
	expression: ObjectExpressionModel | undefined;
	onExpressionChange?: (expression: ObjectExpressionModel | undefined) => void;
	variableScope: IVariableScope | undefined;
	expectedObjectType?: EntityMetadataModel; // when undefined, allows dynamic add/remove of properties
};

function getFreePropertyMetadata(name: string): ParameterMetadata {
	return {
		name,
		displayName: name,
		type: ParameterType.Expression,
		order: 0,
		isCollection: false,
	} as ParameterMetadata;
}

type PropertyInfoCellProps = {
	name: string;
	typeName: string;
	description?: string;
	isRequired?: boolean;
};

function PropertyInfoCell({ name, typeName, description, isRequired }: PropertyInfoCellProps) {
	const trimmedDescription = description?.trim();
	const shouldShowDescription = !!trimmedDescription && trimmedDescription.toLowerCase() !== name.toLowerCase();

	return (
		<TableCell component="th" scope="row" sx={PROPERTY_COLUMN_SX}>
			<Typography variant="body2" component="div" sx={{ fontFamily: codeFontFamily, fontWeight: 600, display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
				<Box component="span" title={name} sx={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				{name}
				</Box>
				{isRequired && (
					<Typography component="span" color="error" sx={{ flexShrink: 0, fontSize: 'inherit', lineHeight: 'inherit' }}>
						*
					</Typography>
				)}
			</Typography>
			<Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, fontFamily: codeFontFamily }}>
				{typeName}
			</Typography>
			{shouldShowDescription && (
				<Typography variant="caption" color="text.primary" component="div" sx={{ mt: 0.5 }}>
					{trimmedDescription}
				</Typography>
			)}
		</TableCell>
	);
}

function AssignmentArrowCell() {
	return (
		<TableCell sx={ASSIGNMENT_COLUMN_SX} align="center">
			<ArrowBackIcon sx={ASSIGNMENT_ARROW_SX} titleAccess="Assigned to property" />
		</TableCell>
	);
}

type RowActionsCellProps = {
	expression: ExpressionModel | undefined;
	onPaste?: (expression: ExpressionModel) => void;
	onRemove?: () => void;
	removeTitle?: string;
};

function RowActionsCell({ expression, onPaste, onRemove, removeTitle }: RowActionsCellProps) {
	return (
		<TableCell align="center" sx={ACTIONS_COLUMN_SX}>
			<Box sx={ACTIONS_CELL_CONTENT_SX}>
				<ExpressionCopyPasteActions expression={expression} onPaste={onPaste} />
				{onRemove && (
					<IconButton size="small" color="error" onClick={onRemove} title={removeTitle}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				)}
			</Box>
		</TableCell>
	);
}

export default function ExpressionObjectPlaceholder({ expression, onExpressionChange, variableScope, expectedObjectType }: ExpressionObjectPlaceholderProps) {
	// Free-form mode state (when expectedObjectType is undefined)
	const [newPropertyName, setNewPropertyName] = useState('');
	const [freePropertyNames, setFreePropertyNames] = useState<string[]>([]);

	// Update freePropertyNames when schema or expression changes
	useEffect(() => {
		// Use case-insensitive comparison to prevent duplicates when schema properties and expression properties differ in casing
		const definedNamesLower = new Set(expectedObjectType?.properties.map((p) => p.name.toLowerCase()) ?? []);
		const free = Object.keys(expression?.Properties ?? {}).filter((k) => !definedNamesLower.has(k.toLowerCase()));
		setFreePropertyNames(free);
	}, [expectedObjectType, expression]);

	// Auto-initialize required object properties on first load only (when expression is undefined)
	useEffect(() => {
		if (!expectedObjectType) return;
		if (expression !== undefined) return;

		const initialProps: Record<string, ExpressionModel> = {};
		for (const p of expectedObjectType.properties) {
			if (p.isRequired && p.referenceEntity) {
				initialProps[p.name] = createObjectMapExpression();
			}
		}
		onExpressionChange?.(createObjectMapExpression(initialProps));
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// --- Free-form handlers ---
	const handleAddFreeProperty = () => {
		const name = newPropertyName.trim();
		if (!name || freePropertyNames.includes(name)) return;
		// Case-insensitive check to prevent duplicates
		if (expectedObjectType?.properties.some((p) => p.name.toLowerCase() === name.toLowerCase())) return;
		setFreePropertyNames((prev) => [...prev, name]);
		setNewPropertyName('');
	};

	const handleRemoveFreeProperty = (name: string) => {
		setFreePropertyNames((prev) => prev.filter((n) => n !== name));
		const newProperties = { ...expression?.Properties };
		delete newProperties[name];
		onExpressionChange?.(updateObjectMapExpression(expression, { properties: newProperties }));
	};

	const handleFreePropertyExpressionChange = (name: string, propertyExpression: ExpressionModel | undefined) => {
		const newProperties = { ...expression?.Properties };
		if (propertyExpression === undefined) {
			delete newProperties[name];
		} else {
			newProperties[name] = propertyExpression;
		}
		onExpressionChange?.(updateObjectMapExpression(expression, { properties: newProperties }));
	};

	const definedProperties = expectedObjectType?.properties ?? [];
	const dictionaryValue = (expectedObjectType as ObjectSchemaType | undefined)?.additionalProperties;
	const longestPropertyNameLength = Math.max(0, ...definedProperties.map((property) => property.name.length), ...freePropertyNames.map((name) => name.length));
	const propertyColumnWidth = Math.min(260, Math.max(160, longestPropertyNameLength * 9 + 48));
	const isEmpty = definedProperties.length === 0 && freePropertyNames.length === 0;
	const isAddDisabled =
		!newPropertyName.trim() ||
		freePropertyNames.includes(newPropertyName.trim()) ||
		// Case-insensitive check to prevent duplicates
		!!expectedObjectType?.properties.some((p) => p.name.toLowerCase() === newPropertyName.trim().toLowerCase());

	return (
		<TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.default', borderRadius: 1.5, boxShadow: 'none' }}>
			<Table
				sx={{
					width: '100%',
					minWidth: 0,
					tableLayout: 'fixed',
					'& .MuiTableCell-root': {
						px: 1,
						py: 1,
						verticalAlign: 'top',
						borderColor: 'divider',
					},
					'& .MuiTableHead-root .MuiTableCell-root': { bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, py: 0.75 },
					'& .MuiTableBody-root > .MuiTableRow-root:hover': { bgcolor: 'action.hover' },
				}}
				aria-label="object properties table"
			>
				<colgroup>
					<col style={{ width: propertyColumnWidth }} />
					<col style={{ width: 28 }} />
					<col />
					<col style={{ width: 96 }} />
				</colgroup>
				<TableHead>
					<TableRow>
						<TableCell sx={PROPERTY_COLUMN_SX}>Property</TableCell>
						<TableCell sx={ASSIGNMENT_COLUMN_SX}></TableCell>
						<TableCell>Value</TableCell>
						<TableCell sx={ACTIONS_COLUMN_SX} align="center"></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isEmpty && (
						<TableRow>
							<TableCell colSpan={4} align="center">
								<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
									{expectedObjectType ? 'No properties defined for this object type' : 'No properties defined. Add a property below.'}
								</Typography>
							</TableCell>
						</TableRow>
					)}
					{definedProperties.map((property) => {
						const key = getCaseInsensitivePropertyKey(expression?.Properties, property.name);
						const propExpr = key !== undefined ? expression?.Properties[key] : undefined;
						const isNestedObjectProperty =
							!(property as ObjectPropertyType).jsonSchema && (!!property.referenceEntity || property.type === PrimitiveType.Object);
						const shouldHideRowActions = isNestedObjectProperty && propExpr !== undefined;

						return (
							<TableRow key={property.name}>
								<PropertyInfoCell
									name={property.name}
									typeName={getPropertyTypeName(property)}
									description={property.description}
									isRequired={property.isRequired}
								/>
								<AssignmentArrowCell />
								<TableCell colSpan={shouldHideRowActions ? 2 : 1}>
									{isNestedObjectProperty ? (
										<ExpressionObjectNestedPlaceholder
											property={property}
											expression={expression}
											onExpressionChange={onExpressionChange}
											expectedObjectType={expectedObjectType!}
											variableScope={variableScope}
										/>
									) : (
										<ExpressionPlaceholder
											variableScope={variableScope}
											parameterMetadata={getPropertyParameterMetadata(property)}
											parameterEnum={property.referenceEnum}
											expression={propExpr}
											onExpressionChange={(propertyExpression) => {
												const newExpression = getObjectExpression(expression, property, propertyExpression, expectedObjectType!);
												onExpressionChange?.(newExpression);
											}}
											isEditable={true}
											fullWidth
										/>
									)}
								</TableCell>
								{!shouldHideRowActions && (
									<RowActionsCell
										expression={propExpr}
										onPaste={
											onExpressionChange
												? (pastedExpr) => {
														const newExpr = getObjectExpression(expression, property, pastedExpr, expectedObjectType!);
														onExpressionChange(newExpr);
													}
												: undefined
										}
									/>
								)}
							</TableRow>
						);
					})}
					{freePropertyNames.map((propName) => (
						<TableRow key={propName}>
							<PropertyInfoCell name={propName} typeName={dictionaryValue ? getPropertyTypeName(dictionaryValue) : 'custom'} />
							<AssignmentArrowCell />
							<TableCell>
								<ExpressionPlaceholder
									variableScope={variableScope}
									parameterMetadata={
										dictionaryValue ? getPropertyParameterMetadata({ ...dictionaryValue, name: propName, isRequired: true }) : getFreePropertyMetadata(propName)
									}
									expression={expression?.Properties?.[propName]}
									onExpressionChange={(expr) => handleFreePropertyExpressionChange(propName, expr)}
									isEditable={true}
									fullWidth
								/>
							</TableCell>
							<RowActionsCell
								expression={expression?.Properties?.[propName]}
								onPaste={(pastedExpr) => handleFreePropertyExpressionChange(propName, pastedExpr)}
								onRemove={() => handleRemoveFreeProperty(propName)}
								removeTitle={`Remove property "${propName}"`}
							/>
						</TableRow>
					))}
					{(expectedObjectType as ObjectSchemaType | undefined)?.allowsAdditionalProperties !== false && (
						<TableRow>
							<TableCell colSpan={4}>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<TextField
										size="small"
										placeholder="New property name"
										value={newPropertyName}
										onChange={(e) => setNewPropertyName(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') handleAddFreeProperty();
										}}
										fullWidth
										slotProps={{
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<IconButton size="small" onClick={handleAddFreeProperty} disabled={isAddDisabled} title="Add property">
															<AddCircleIcon fontSize="small" />
														</IconButton>
													</InputAdornment>
												),
											},
										}}
									/>
								</Box>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
